import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Space,
  Popconfirm,
  Row,
  Col,
  Typography,
  Tag
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  SettingOutlined,
  UserOutlined
} from '@ant-design/icons';
import { useToast } from '../../../contexts/ToastContext';
import { apiService } from '../../../services/api';
import { MentorSkillResponse, CreateMentorSkillRequest, UpdateMentorSkillRequest } from '../../../types';
import { mentorProfileQueryKeys } from '../../../lib/queryClient';
import PanelTabCard from '../../../components/Common/PanelTabCard';
import TabListLoading from '../../../components/Common/TabListLoading';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const SkillsTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSkill, setEditingSkill] = useState<MentorSkillResponse | null>(null);
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();

  const skillLevels = [
    { value: 'Beginner', label: 'Başlangıç' },
    { value: 'Intermediate', label: 'Orta' },
    { value: 'Advanced', label: 'İleri' },
    { value: 'Expert', label: 'Uzman' }
  ];

  const { data: skills = [], isLoading: loading } = useQuery({
    queryKey: mentorProfileQueryKeys.skills,
    queryFn: async () => {
      const response = await apiService.getMentorSkills();
      if (!response.success) throw new Error(response.message);
      return response.data ?? [];
    },
    staleTime: 1000 * 60 * 5,
  });

  const createMutation = useMutation({
    mutationFn: async (data: CreateMentorSkillRequest) => {
      const response = await apiService.createMentorSkill(data);
      if (!response.success) throw new Error(response.message);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Yetkinlik başarıyla eklendi.');
      setModalVisible(false);
      queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.skills });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Hata oluştu.')
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateMentorSkillRequest }) => {
      const response = await apiService.updateMentorSkill(id, data);
      if (!response.success) throw new Error(response.message);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Yetkinlik başarıyla güncellendi.');
      setModalVisible(false);
      queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.skills });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Hata oluştu.')
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await apiService.deleteMentorSkill(id);
      if (!response.success) throw new Error(response.message);
      return response;
    },
    onSuccess: () => {
      toast.success('Yetkinlik başarıyla silindi.');
      queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.skills });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Hata oluştu.')
  });

  const handleAdd = () => {
    setEditingSkill(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (skill: MentorSkillResponse) => {
    setEditingSkill(skill);
    form.setFieldsValue({
      skillName: skill.skillName,
      skillLevel: skill.skillLevel,
      experienceYears: skill.experienceYears,
      description: skill.description
    });
    setModalVisible(true);
  };

  const handleDelete = (id: number) => {
    deleteMutation.mutate(id);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const skillData: CreateMentorSkillRequest & UpdateMentorSkillRequest = {
        skillName: values.skillName,
        skillLevel: values.skillLevel,
        experienceYears: values.experienceYears,
        description: values.description
      };
      if (editingSkill) {
        updateMutation.mutate({ id: editingSkill.id, data: skillData });
      } else {
        createMutation.mutate(skillData);
      }
    } catch {
      // Form validation failed
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingSkill(null);
    form.resetFields();
  };

  const filteredSkills = skills.filter(skill =>
    skill.skillName.toLowerCase().includes(searchText.toLowerCase()) ||
    (skill.skillLevel && skill.skillLevel.toLowerCase().includes(searchText.toLowerCase())) ||
    (skill.description && skill.description.toLowerCase().includes(searchText.toLowerCase()))
  );

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'Beginner': return 'blue';
      case 'Intermediate': return 'green';
      case 'Advanced': return 'orange';
      case 'Expert': return 'red';
      default: return 'default';
    }
  };

  const getLevelLabel = (level: string) => {
    const levelObj = skillLevels.find(l => l.value === level);
    return levelObj ? levelObj.label : level;
  };

  const columns = [
    {
      title: 'Yetkinlik Adı',
      dataIndex: 'skillName',
      key: 'skillName',
      sorter: (a: MentorSkillResponse, b: MentorSkillResponse) =>
        a.skillName.localeCompare(b.skillName),
    },
    {
      title: 'Seviye',
      dataIndex: 'skillLevel',
      key: 'skillLevel',
      filters: skillLevels.map(level => ({ text: level.label, value: level.value })),
      onFilter: (value: boolean | React.Key, record: MentorSkillResponse) => record.skillLevel === value,
      render: (level: string) => (
        <Tag color={getLevelColor(level)}>
          {getLevelLabel(level)}
        </Tag>
      ),
    },
    {
      title: 'Deneyim (Yıl)',
      dataIndex: 'experienceYears',
      key: 'experienceYears',
      sorter: (a: MentorSkillResponse, b: MentorSkillResponse) =>
        (a.experienceYears || 0) - (b.experienceYears || 0),
      render: (years: number) => years ? `${years} yıl` : '-',
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: MentorSkillResponse) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
            style={{
              backgroundColor: '#8b949e',
              borderColor: '#8b949e',
              color: 'white',
              fontWeight: 500
            }}
          >
            Düzenle
          </Button>
          <Popconfirm
            title="Bu yetkinliği silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
            okButtonProps={{
              style: {
                backgroundColor: '#ef4444',
                borderColor: '#ef4444',
                color: 'white',
                fontWeight: 500
              }
            }}
            cancelButtonProps={{
              style: {
                backgroundColor: 'white',
                borderColor: '#d1d5db',
                color: 'var(--text-secondary)',
                fontWeight: 500
              }
            }}
          >
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              size="small"
              style={{
                backgroundColor: '#ef4444',
                borderColor: '#ef4444',
                color: 'white',
                fontWeight: 500
              }}
            >
              Sil
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
    <PanelTabCard
      title="Yetkinlikler"
      description="Yetkinliklerinizi ve deneyim seviyenizi ekleyin veya düzenleyin."
    >
      <style>
        {`
          .ant-btn:not(.ant-btn-primary):hover {
            background-color: inherit !important;
            border-color: inherit !important;
            color: inherit !important;
            transform: none !important;
            box-shadow: none !important;
          }
          .ant-btn:not(.ant-btn-primary):focus {
            background-color: inherit !important;
            border-color: inherit !important;
            color: inherit !important;
            transform: none !important;
            box-shadow: none !important;
          }
          .ant-btn:not(.ant-btn-primary):active {
            background-color: inherit !important;
            border-color: inherit !important;
            color: inherit !important;
            transform: none !important;
            box-shadow: none !important;
          }
        `}
      </style>
      <TabListLoading loading={loading}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
        </Col>
        <Col>
          <Space>
            <Input
              placeholder="Ara..."
              allowClear
              style={{ width: 300 }}
              onChange={(e) => setSearchText(e.target.value)}
              prefix={<SearchOutlined />}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              style={{
                backgroundColor: '#8b949e',
                borderColor: '#8b949e',
                color: 'white',
                fontWeight: 500
              }}
            >
              Yetkinlik Ekle
            </Button>
          </Space>
        </Col>
      </Row>

      <Table
        scroll={{ x: 'max-content' }}
        columns={columns}
        dataSource={filteredSkills}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSizeOptions: ['5', '10', '20', '50'],
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} / ${total} yetkinlik`,
        }}
      />
      </TabListLoading>

      <Modal
        title={editingSkill ? 'Yetkinlik Düzenle' : 'Yeni Yetkinlik'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={600}
        okText={editingSkill ? 'Güncelle' : 'Ekle'}
        cancelText="İptal"
        okButtonProps={{
          style: {
            backgroundColor: 'var(--text-secondary)',
            borderColor: 'var(--text-secondary)',
            color: 'white',
            fontWeight: 500,
            height: '32px',
            minWidth: '80px',
            visibility: 'visible',
            opacity: 1,
            filter: 'none'
          }
        }}
        cancelButtonProps={{
          style: {
            backgroundColor: 'white',
            borderColor: '#d1d5db',
            color: 'var(--text-secondary)',
            fontWeight: 500,
            height: '32px',
            minWidth: '80px',
            visibility: 'visible',
            opacity: 1,
            filter: 'none'
          }
        }}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="skillName"
            label="Yetkinlik Adı"
            rules={[{ required: true, message: 'Lütfen yetkinlik adını girin!' }]}
          >
            <Input prefix={<SettingOutlined />} placeholder="Örn: JavaScript, React, Python" />
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="skillLevel"
                label="Seviye"
              >
                <Select placeholder="Seviye seçin">
                  {skillLevels.map(level => (
                    <Option key={level.value} value={level.value}>
                      {level.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="experienceYears"
                label="Deneyim (Yıl)"
              >
                <InputNumber
                  min={0}
                  max={50}
                  style={{ width: '100%' }}
                  placeholder="Kaç yıl deneyim?"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Açıklama"
          >
            <TextArea rows={3} placeholder="Bu yetkinlik hakkında kısa bir açıklama" />
          </Form.Item>
        </Form>
      </Modal>
    </PanelTabCard>
    </>
  );
};

export default SkillsTab;
